// Global toast provider.
//
// Several pages already roll their own inline `useToast` / `Toast` combo
// (HomeDashboard, LeadDetailV2, Marketplace, AdminPanel, SeatingChartTab)
// and pass `showToast` down as a prop. Those work
// and aren't refactored here — the goal of this module is to replace
// the ~18 remaining `window.alert()` calls that live in pages that
// never had a local toast system, without forcing every caller to
// prop-drill one in.
//
// Usage:
//   import { useToast } from './useToast'
//   const { showToast } = useToast()
//   showToast('Saved.')                                  // info (ink pill)
//   showToast('Upload failed.', { type: 'error' })       // red pill
//   showToast('Invitation sent.', { type: 'success' })   // sage pill
//
// Stacks bottom-center (matches the LeadDetailV2 style that's closest
// to the Editorial Atelier direction). Click a toast to dismiss it
// early. Auto-dismiss after 2.8s (info/success) / 4s (error) so
// failure copy sticks around long enough to read.
//
// The hook + context live in ./useToast.js so this file exports only
// a component (Vite Fast Refresh requirement).
import React, { useCallback, useRef, useState } from 'react'
import { ToastContext } from './useToast'

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const showToast = useCallback((message, opts = {}) => {
    if (!message) return null
    const id = ++idRef.current
    const type = opts.type || 'info'
    // Errors linger a beat longer so the user can actually read them
    // before they vanish.
    const duration = opts.duration ?? (type === 'error' ? 4000 : 2800)
    setToasts(prev => [...prev, { id, message, type }])
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
    return id
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            // Offset above the iPhone home-bar gesture zone so toasts
            // aren't tappable-behind / clipped by system UI.
            bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            zIndex: 10000,
            pointerEvents: 'none',
            // Prevent a single huge toast from stretching off-screen
            // on narrow viewports.
            maxWidth: 'min(560px, calc(100vw - 32px))',
            width: 'max-content',
            alignItems: 'center',
          }}
        >
          {toasts.map(t => (
            <ToastItem key={t.id} {...t} onDismiss={() => dismissToast(t.id)} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}

function ToastItem({ message, type, onDismiss }) {
  const palette = {
    info: { background: 'var(--ink, #1A1D23)', color: 'var(--paper, #FAFAF7)' },
    success: { background: 'var(--sage, #6b8e7f)', color: '#ffffff' },
    error: { background: '#b91c1c', color: '#ffffff' },
  }
  const style = palette[type] || palette.info
  return (
    <button
      type="button"
      onClick={onDismiss}
      style={{
        ...style,
        padding: '10px 20px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 500,
        border: 'none',
        fontFamily: 'inherit',
        boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
        pointerEvents: 'auto',
        cursor: 'pointer',
        textAlign: 'center',
        lineHeight: 1.35,
        maxWidth: '100%',
        wordBreak: 'break-word',
      }}
      aria-label={`${type} toast. Click to dismiss.`}
    >
      {message}
    </button>
  )
}
